import { useState } from 'react';

export interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  title?: string;
  items: FAQItem[];
}

export default function FAQSection({
  title = 'Frequently Asked Questions',
  items,
}: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="bg-white py-24 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-slate-100" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-slate-100" />

      <div className="relative z-10 max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full text-orange-700 text-xs font-bold uppercase tracking-[0.2em] border border-orange-200 bg-orange-50 mb-4">
            Got Questions?
          </span>
          <h2 className="font-display text-4xl lg:text-5xl font-black text-slate-900 mb-4">{title}</h2>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 border border-slate-200">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-mic-fill text-orange-500 text-sm" />
            </div>
            <span className="text-slate-600 text-xs font-bold">Voice Search &amp; AI Optimized</span>
          </div>
        </div>

        {/* FAQ items */}
        <div className="space-y-3">
          {items.map((item, index) => (
            <div
              key={index}
              className={`rounded-2xl overflow-hidden border transition-all duration-300 ${
                openIndex === index
                  ? 'border-orange-300 bg-orange-50'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <button
                className="w-full flex items-center justify-between px-7 py-5 text-left cursor-pointer"
                onClick={() => toggle(index)}
              >
                <div className="flex items-center gap-3 pr-4 flex-1 min-w-0">
                  <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${openIndex === index ? 'text-orange-500' : 'text-slate-400'}`}>
                    <i className="ri-question-fill text-lg" />
                  </div>
                  <h3 className="font-display font-bold text-slate-900 text-base">{item.question}</h3>
                </div>
                <div
                  className={`w-8 h-8 flex items-center justify-center rounded-full border flex-shrink-0 transition-all duration-300 ${
                    openIndex === index
                      ? 'bg-orange-500 border-orange-500 rotate-45'
                      : 'border-slate-300 text-slate-400'
                  }`}
                >
                  <i className={`ri-add-line text-sm ${openIndex === index ? 'text-white' : ''}`} />
                </div>
              </button>

              {openIndex === index && (
                <div className="px-7 pb-6">
                  <div className="w-full h-px bg-orange-200 mb-4 ml-8" />
                  <p className="text-slate-600 leading-relaxed text-sm pl-8">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* FAQ bottom CTA */}
        <div className="mt-12 text-center p-8 rounded-2xl border border-slate-200 bg-slate-50">
          <p className="text-slate-900 font-display font-bold text-lg mb-2">Still have questions?</p>
          <p className="text-slate-500 text-sm mb-5">Our team is available 7 days a week to help with any questions about installation or pricing.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="tel:+18005551234"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-900 text-white font-black text-sm hover:bg-slate-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill" />
              Call (800) 555-1234
            </a>
            <a
              href="mailto:info@secure2ware.com"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-slate-200 text-slate-700 font-semibold text-sm hover:border-orange-300 hover:text-orange-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill text-orange-500" />
              Email Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
